import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { AppDispatch } from "@redux/store/store";
import { setSelectedProduct } from "@redux/slices/productSlice";
import { NormalizedProduct, normalizeProduct } from "../productConfig";
import {
  determineSelectedProduct,
  getFilteredServiceItems,
  handleSelection,
} from "../MenuUtils";
import { serviceItems } from "../MenuData";
import type { MenuItem } from "../MenuData";

type ProductListEntry = { id: number; name: string };

/**
 * Product dropdown state, service-item filtering, and internal name for the current selection.
 */
export const useSideMenuProductSelection = (
  productList: ProductListEntry[] | undefined,
  dispatch: AppDispatch
) => {
  const location = useLocation();
  const navigate = useNavigate();

  const normalizedProducts = useMemo(() => {
    return (productList ?? [])
      .map(normalizeProduct)
      .filter((p): p is NormalizedProduct => p !== null);
  }, [productList]);

  const productOptions = useMemo(
    () => normalizedProducts.map((p) => p.displayName),
    [normalizedProducts]
  );

  const displayNameToProduct = useMemo(() => {
    const map: Record<string, NormalizedProduct> = {};
    normalizedProducts.forEach((product) => {
      map[product.displayName] = product;
    });
    return map;
  }, [normalizedProducts]);

  const [selectedProduct, setSelectedProductLocal] = useState("Select Product");

  useEffect(() => {
    if (normalizedProducts.length === 0) {
      setSelectedProductLocal("Select Product");
      return;
    }

    const routeInternalName = determineSelectedProduct(location.pathname);

    if (routeInternalName) {
      const matchingProduct = normalizedProducts.find(
        (p) => p.internalName === routeInternalName
      );

      if (matchingProduct) {
        setSelectedProductLocal(matchingProduct.displayName);
        dispatch(setSelectedProduct(matchingProduct.internalName));
        return;
      }
    }

    if (
      selectedProduct === "Select Product" ||
      !displayNameToProduct[selectedProduct]
    ) {
      const firstProduct = normalizedProducts[0];
      setSelectedProductLocal(firstProduct.displayName);
      dispatch(setSelectedProduct(firstProduct.internalName));
    }
  }, [
    location.pathname,
    normalizedProducts,
    selectedProduct,
    displayNameToProduct,
    dispatch,
  ]);

  const [filteredServiceItems, setFilteredServiceItems] = useState<MenuItem[]>([]);
  useEffect(() => {
    const product = displayNameToProduct[selectedProduct];
    const internal = product?.internalName || selectedProduct;
    setFilteredServiceItems(getFilteredServiceItems(internal, serviceItems));
  }, [selectedProduct, displayNameToProduct]);

  const onSelectProduct = (selected: string) => {
    const product = displayNameToProduct[selected];
    if (!product) return;
    setSelectedProductLocal(selected);
    dispatch(setSelectedProduct(product.internalName));
    handleSelection(product.internalName, navigate);
  };

  const currentProduct = useMemo(
    () => displayNameToProduct[selectedProduct],
    [selectedProduct, displayNameToProduct]
  );

  const internalName = currentProduct?.internalName || selectedProduct;

  return {
    productOptions,
    selectedProduct,
    onSelectProduct,
    displayNameToProduct,
    filteredServiceItems,
    internalName,
  };
};
